/**
 * IntiRechargeModal — Recarga de créditos IA con Stripe
 * 3 paquetes + recarga libre mín. $10 USD
 */
import React, { useState, useEffect } from 'react';
import { X, Zap, CheckCircle2, ArrowRight, Loader2, Gift } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  {
    id: 'inti_basico',
    name: 'Básico',
    intis: 500,
    price: 5.00,
    desc: 'Ideal para empezar',
    color: 'text-emerald-400',
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/8',
    glow: 'hover:shadow-[0_0_24px_rgba(34,197,94,0.15)]',
    popular: false,
  },
  {
    id: 'inti_pro',
    name: 'Pro',
    intis: 2000,
    price: 20.00,
    desc: 'El más elegido por agencias',
    color: 'text-primary',
    border: 'border-primary/50',
    bg: 'bg-primary/10',
    glow: 'hover:shadow-[0_0_28px_rgba(0,229,255,0.2)]',
    popular: true,
  },
  {
    id: 'inti_agency',
    name: 'Agency',
    intis: 10000,
    price: 100.00,
    desc: 'Para toda tu cartera de clientes',
    color: 'text-purple-400',
    border: 'border-purple-500/30',
    bg: 'bg-purple-500/8',
    glow: 'hover:shadow-[0_0_24px_rgba(124,58,237,0.15)]',
    popular: false,
  },
];

export default function IntiRechargeModal({ clientId, onClose, authFetch }) {
  const [selected, setSelected] = useState('inti_pro');
  const [customUsd, setCustomUsd] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);

  const selPack = PACKAGES.find(p => p.id === selected);
  const customIntis = useCustom && customUsd >= 10 ? Math.floor(parseFloat(customUsd) * 100) : 0;
  const displayIntis = useCustom ? customIntis : selPack?.intis || 0;
  const displayPrice = useCustom ? parseFloat(customUsd || 0) : selPack?.price || 0;

  const checkout = async () => {
    if (useCustom && (!customUsd || parseFloat(customUsd) < 10)) {
      toast.error('El monto mínimo es $10 USD');
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const body = useCustom
        ? { custom_usd: parseFloat(customUsd), client_id: clientId, origin_url: origin }
        : { package_id: selected, client_id: clientId, origin_url: origin };
      const r = await authFetch(`${API}/inti/recharge/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al crear el pago');
      toast.success('Redirigiendo a Stripe...');
      window.location.href = j.checkout_url;
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-xl rounded-3xl border border-border-subtle shadow-2xl overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #060D1F 0%, #0A1A3F 100%)' }}
        data-testid="inti-recharge-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-text-heading">Recargar Intis</h3>
                <p className="text-xs text-text-muted">Cliente: {clientId} · 1 inti = $0.01 USD</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-text-muted hover:text-text-heading transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Package cards */}
          <div className="grid grid-cols-3 gap-3">
            {PACKAGES.map(p => (
              <button
                key={p.id}
                onClick={() => { setSelected(p.id); setUseCustom(false); }}
                data-testid={`inti-pkg-${p.id}`}
                className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all ${
                  selected === p.id && !useCustom
                    ? `${p.border} ${p.bg} ${p.glow}`
                    : 'border-border-subtle hover:border-primary/20'
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold px-2 py-0.5 rounded-full bg-primary text-[#0A1A3F] whitespace-nowrap">
                    MÁS POPULAR
                  </span>
                )}
                <p className={`text-base font-heading font-bold ${p.color}`}>{p.name}</p>
                <p className={`text-2xl font-heading font-extrabold ${selected === p.id && !useCustom ? p.color : 'text-text-heading'}`}>
                  {p.intis.toLocaleString()}
                </p>
                <p className="text-[10px] text-text-muted">intis</p>
                <p className="text-lg font-bold text-text-heading">${p.price}</p>
                <p className="text-[10px] text-text-muted text-center leading-tight">{p.desc}</p>
                {selected === p.id && !useCustom && (
                  <CheckCircle2 className={`w-4 h-4 ${p.color} mt-1`} />
                )}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div
            onClick={() => setUseCustom(true)}
            className={`p-4 rounded-2xl border cursor-pointer transition-all ${
              useCustom ? 'border-amber-500/40 bg-amber-500/8' : 'border-border-subtle hover:border-amber-500/20'
            }`}
            data-testid="inti-custom-toggle"
          >
            <div className="flex items-center gap-3">
              <Gift className={`w-5 h-5 ${useCustom ? 'text-amber-400' : 'text-text-muted'}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${useCustom ? 'text-amber-300' : 'text-text-muted'}`}>
                  Recarga personalizada (mín. $10 USD)
                </p>
                {useCustom && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-amber-400 font-bold">$</span>
                    <input
                      type="number"
                      min="10"
                      step="1"
                      placeholder="Monto en USD"
                      value={customUsd}
                      onChange={e => setCustomUsd(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      className="input-base flex-1 text-sm py-1.5"
                      data-testid="inti-custom-amount"
                      autoFocus
                    />
                    {customIntis > 0 && (
                      <span className="text-xs text-amber-400 whitespace-nowrap">{customIntis.toLocaleString()} intis</span>
                    )}
                  </div>
                )}
              </div>
              {useCustom && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/15 flex items-center justify-between">
            <div>
              <p className="text-xs text-text-muted">Recibirás</p>
              <p className="text-2xl font-heading font-bold text-primary mt-0.5">
                {displayIntis.toLocaleString()} <span className="text-sm font-normal">intis</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-text-muted">Total a pagar</p>
              <p className="text-2xl font-heading font-bold text-text-heading mt-0.5">${displayPrice.toFixed(2)}</p>
              <p className="text-[10px] text-text-muted">USD · Pago seguro vía Stripe</p>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={checkout}
            disabled={loading || (useCustom && (!customUsd || parseFloat(customUsd) < 10))}
            className="w-full btn-primary py-3.5 text-base font-semibold flex items-center justify-center gap-3 disabled:opacity-40"
            data-testid="inti-checkout-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Pagar ${displayPrice.toFixed(2)} y recargar {displayIntis.toLocaleString()} intis
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-[10px] text-text-muted">
            Pago 100% seguro con Stripe · Los intis se acreditan automáticamente en segundos
          </p>
        </div>
      </div>
    </div>
  );
}
